import React, { useMemo, useRef, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { isActivelySeekingRecent } from "@/lib/utils";
import type { Profile, AlumniStats } from "@workspace/api-client-react";

type Tab = "talent" | "opportunities";

interface DynamicHeaderProps {
  tab: Tab;
  stats?: AlumniStats;
  statsLoading: boolean;
  statsError: boolean;
  profiles?: Profile[];
  profilesLoading: boolean;
  onTitleFilter: (title: string) => void;
  isVerifiedAlumni?: boolean;
  referralsAvailable?: number;
}

interface TickerItem {
  title: string;
  seeking: boolean;
}

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

const MIN_TICKER_ITEMS = 25;
// Hard cap so the doubled marquee track never exceeds ~60 DOM nodes regardless
// of directory size. 30 items × ~350 px = ~10 500 px — well above the widest
// viewport, so the seamless loop point is always off-screen.
const MAX_TICKER_ITEMS = 30;
const PX_PER_ITEM = 350;
const TARGET_SPEED_PX_PER_S = 80;

export default function DynamicHeader({
  tab,
  stats,
  statsLoading,
  statsError,
  profiles,
  profilesLoading,
  onTitleFilter,
  isVerifiedAlumni,
  referralsAvailable,
}: DynamicHeaderProps) {
  const profileSignature = profiles
    ?.map((p) => `${p.id}:${p.activelySeekingUpdatedAt ?? ""}`)
    .join("|") ?? "";

  const displayItems = useMemo((): TickerItem[] => {
    if (!profiles?.length) return [];

    const seen = new Set<string>();
    const seekingItems: TickerItem[] = [];
    const otherItems: TickerItem[] = [];

    for (const p of profiles) {
      // Pick a random title from this profile's titles list (or fall back to the single title)
      const titlePool = p.titles && p.titles.length > 0 ? p.titles : (p.title ? [p.title] : []);
      if (titlePool.length === 0) continue;
      const pickedTitle = titlePool[Math.floor(Math.random() * titlePool.length)]!;
      if (!pickedTitle || seen.has(pickedTitle)) continue;
      seen.add(pickedTitle);
      if (isActivelySeekingRecent(p.activelySeekingUpdatedAt)) {
        seekingItems.push({ title: pickedTitle, seeking: true });
      } else {
        otherItems.push({ title: pickedTitle, seeking: false });
      }
    }

    const shuffledSeeking = shuffleArray(seekingItems);
    const shuffledOthers = shuffleArray(otherItems);

    // --- Distribution strategy ---
    // Seeking items are scattered evenly among non-seeking ones using
    // Bresenham-style placement so glowing titles never clump together.
    //
    // When seeking items would dominate (> MAX_SEEK_RATIO of the ticker),
    // we cap how many are shown and force non-seeking filler in for visual
    // contrast. When there are very few seeking items they get wide spacing.

    const MAX_SEEK_RATIO = 0.65;

    if (shuffledSeeking.length === 0) {
      // No seeking items — pad others to minimum by cycling
      const base = shuffledOthers.length > 0 ? shuffledOthers : [];
      if (base.length === 0) return [];
      const result: TickerItem[] = [];
      while (result.length < MIN_TICKER_ITEMS) result.push(...base);
      return result.slice(0, Math.min(Math.max(base.length, MIN_TICKER_ITEMS), MAX_TICKER_ITEMS));
    }

    if (shuffledOthers.length === 0) {
      // Nothing to interleave — return all seeking items
      return shuffledSeeking.slice(0, MAX_TICKER_ITEMS);
    }

    // Cap seeking count so seeking/total <= MAX_SEEK_RATIO
    // seeking <= MAX_SEEK_RATIO * others / (1 - MAX_SEEK_RATIO)
    const maxSeekByRatio = Math.max(
      1,
      Math.floor((MAX_SEEK_RATIO * shuffledOthers.length) / (1 - MAX_SEEK_RATIO)),
    );
    const seekCount = Math.min(shuffledSeeking.length, maxSeekByRatio);

    // How many non-seeking items to include:
    // - enough to maintain the ratio floor
    // - enough to reach MIN_TICKER_ITEMS total
    const minOtherForRatio = Math.ceil(
      (seekCount * (1 - MAX_SEEK_RATIO)) / MAX_SEEK_RATIO,
    );
    const minOtherForTotal = Math.max(0, MIN_TICKER_ITEMS - seekCount);
    const otherCount = Math.min(
      shuffledOthers.length,
      Math.max(minOtherForRatio, minOtherForTotal),
    );

    const total = seekCount + otherCount;

    // Place each seeking item at an evenly distributed position within [0, total).
    // Formula: floor((i + 0.5) * total / seekCount) centres the slots so the
    // first and last glowing items aren't flush against the edges.
    const seekPositions = new Set<number>();
    for (let i = 0; i < seekCount; i++) {
      seekPositions.add(
        Math.min(Math.floor(((i + 0.5) * total) / seekCount), total - 1),
      );
    }

    const result: TickerItem[] = [];
    let si = 0;
    let oi = 0;
    for (let pos = 0; pos < total; pos++) {
      if (seekPositions.has(pos) && si < seekCount) {
        result.push(shuffledSeeking[si++]!);
      } else {
        // Cycle through others if there aren't enough to fill every gap
        result.push(shuffledOthers[oi % shuffledOthers.length]!);
        oi++;
      }
    }

    // Cap to MAX_TICKER_ITEMS so the doubled marquee track never exceeds ~60 DOM nodes
    return result.slice(0, MAX_TICKER_ITEMS);
  // Dep on the signature string so same-data refetches don't re-shuffle and restart the animation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileSignature]);

  // Estimated duration for the initial render (before layout is measured).
  // Kept as a plain number so it can seed animDurationRef and the initial style prop.
  const estimatedDurationS = Math.max(
    20,
    (displayItems.length * PX_PER_ITEM) / TARGET_SPEED_PX_PER_S,
  );

  // Mutable ref holds the latest measured duration so the touch handler closure
  // always reads the correct value without needing to re-bind on every measurement.
  const animDurationRef = useRef(estimatedDurationS);

  // --- Touch swipe-to-drag for mobile ---
  const touchWrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const swipeRef = useRef({
    startX: 0,
    startAnimX: 0,
    swiping: false,
    resumeTimer: null as ReturnType<typeof setTimeout> | null,
  });

  // After each render that produces items, measure the real track width and
  // correct the animation duration so speed stays at TARGET_SPEED_PX_PER_S
  // regardless of how wide the titles actually render.
  useEffect(() => {
    if (displayItems.length === 0) return;
    const raf = requestAnimationFrame(() => {
      const el = trackRef.current;
      if (!el) return;
      // The track is doubled (items × 2) for the seamless loop; half-width is
      // one full loop distance, which the CSS animation traverses in `duration` s.
      const half = el.scrollWidth / 2;
      if (half === 0) return;
      const measured = Math.max(20, half / TARGET_SPEED_PX_PER_S);
      animDurationRef.current = measured;

      // Preserve the current visual playhead position so changing the duration
      // doesn't cause a visible jump or backwards snap.  When the browser
      // receives a new animation-duration mid-play it recalculates the playhead
      // as elapsedTime / newDuration — which snaps to a different point.
      // Instead, read the live translateX from the computed matrix, convert it
      // to a [0,1] progress fraction over the half-width loop distance, then
      // bake that into a negative animation-delay so the new duration starts
      // from exactly the same visual position.
      let progress = 0;
      try {
        const transform = window.getComputedStyle(el).transform;
        if (transform && transform !== "none") {
          const matrix = new DOMMatrix(transform);
          // translateX is negative; clamp into [0, half] then normalise.
          const translated = Math.abs(matrix.m41);
          progress = Math.min(translated / half, 1);
        }
      } catch {
        // DOMMatrix unavailable (SSR / old browser) — start from 0.
      }

      // Apply directly to the DOM — avoids a React re-render and the resulting
      // animation restart that would come from updating state.
      el.style.animationDelay = `-${progress * measured}s`;
      el.style.animationDuration = `${measured}s`;
    });
    return () => cancelAnimationFrame(raf);
  }, [displayItems]);

  // Passive touch listeners so the browser never waits for JS before processing
  // scroll frames. Deps include tab (wrapper mounts/unmounts on tab change) and
  // displayItems.length (wrapper only renders when there are items, so we
  // re-bind when they first appear). animDurationRef is a ref — no dep needed.
  useEffect(() => {
    if (tab !== "talent") return;
    const wrapper = touchWrapperRef.current;
    if (!wrapper) return;

    const swipe = swipeRef.current;

    const onTouchStart = (e: TouchEvent) => {
      const s = swipeRef.current;
      if (s.resumeTimer !== null) { clearTimeout(s.resumeTimer); s.resumeTimer = null; }
      const el = trackRef.current;
      if (!el) return;
      const t = window.getComputedStyle(el).transform;
      const currentX = (!t || t === "none") ? 0 : new DOMMatrix(t).m41;
      s.startX = e.touches[0]!.clientX;
      s.startAnimX = currentX;
      s.swiping = false;
      el.style.animationPlayState = "paused";
      el.style.transform = `translateX(${currentX}px)`;
    };

    const onTouchMove = (e: TouchEvent) => {
      const el = trackRef.current;
      if (!el) return;
      const s = swipeRef.current;
      const dx = e.touches[0]!.clientX - s.startX;
      if (Math.abs(dx) > 4) s.swiping = true;
      if (!s.swiping) return;
      el.style.transform = `translateX(${s.startAnimX + dx}px)`;
    };

    const onTouchEnd = () => {
      const el = trackRef.current;
      if (!el) return;
      const s = swipeRef.current;
      const match = el.style.transform.match(/-?\d+(\.\d+)?/);
      const currentX = match ? parseFloat(match[0]!) : s.startAnimX;
      s.resumeTimer = setTimeout(() => {
        const el2 = trackRef.current;
        if (!el2) return;
        const half = el2.scrollWidth / 2;
        if (half === 0) { el2.style.transform = ""; el2.style.animationPlayState = "running"; return; }
        // Map currentX into [-half, 0) and derive a matching animation-delay
        let x = currentX % half;
        if (x > 0) x -= half;
        if (x <= -half) x += half;
        // Use the ref so we always have the post-measurement duration
        const delay = -(x / -half) * animDurationRef.current;
        el2.style.animationDelay = `${delay}s`;
        el2.style.transform = "";
        el2.style.animationPlayState = "running";
        s.resumeTimer = null;
      }, 500);
    };

    wrapper.addEventListener("touchstart", onTouchStart, { passive: true });
    wrapper.addEventListener("touchmove", onTouchMove, { passive: true });
    wrapper.addEventListener("touchend", onTouchEnd, { passive: true });
    wrapper.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      wrapper.removeEventListener("touchstart", onTouchStart);
      wrapper.removeEventListener("touchmove", onTouchMove);
      wrapper.removeEventListener("touchend", onTouchEnd);
      wrapper.removeEventListener("touchcancel", onTouchEnd);
      if (swipe.resumeTimer !== null) { clearTimeout(swipe.resumeTimer); swipe.resumeTimer = null; }
    };
  }, [tab, displayItems.length]);

  if (tab === "talent" && isVerifiedAlumni) {
    // Show Verified Members while loading or on error so the skeleton/em-dash
    // still renders; hide only when the count is a confirmed zero.
    const verifiedCount = stats?.verifiedAlumni ?? 0;
    const showVerifiedMembers = statsLoading || statsError || verifiedCount > 0;

    return (
      <section className="border-y border-white/5 bg-zinc-900/30 backdrop-blur py-6 relative z-20">
        <div className="container mx-auto max-w-6xl px-4 flex flex-wrap justify-center md:justify-start items-center gap-10 md:gap-16">
          {showVerifiedMembers && (
            <div className="flex flex-col items-center md:items-start">
              {statsLoading ? (
                <Skeleton className="h-9 w-12 mb-1 bg-white/5" />
              ) : (
                <span className="text-3xl font-bold text-white">
                  {statsError ? "—" : verifiedCount}
                </span>
              )}
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Verified Members</span>
            </div>
          )}

          {showVerifiedMembers && (
            <div className="w-px h-10 bg-white/5 hidden md:block" />
          )}

          <div className="flex flex-col items-center md:items-start">
            {statsLoading ? (
              <Skeleton className="h-9 w-10 mb-1 bg-white/5" />
            ) : (
              <span className="text-3xl font-bold text-white">
                {statsError ? "—" : (stats?.activelySeekingLast30Days ?? 0)}
              </span>
            )}
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Open to Work</span>
          </div>

        </div>
      </section>
    );
  }

  if (tab === "talent") {
    return (
      <section
        className="ticker-section relative border-y border-white/5 overflow-hidden"
        style={{ background: "#0a0a0a", height: "60px", contain: "layout style" }}
      >
        {/* Left pinned label */}
        <div
          className="absolute left-0 top-0 bottom-0 z-10 flex items-center px-5 border-r border-zinc-900"
          style={{
            background: "#0a0a0a",
            boxShadow: "12px 0 24px rgba(10,10,10,0.95)",
          }}
        >
          <span
            className="ticker-glow-label font-semibold uppercase"
            style={{ fontSize: "9px", letterSpacing: "0.18em", fontFamily: "Inter, sans-serif", color: "#E8184A" }}
          >
            Open to<br />Work
          </span>
        </div>

        {profilesLoading ? (
          <div className="flex items-center gap-8 h-full pl-28">
            <Skeleton className="h-8 w-48 bg-white/5" />
            <Skeleton className="h-8 w-64 bg-white/5" />
            <Skeleton className="h-8 w-40 bg-white/5" />
            <Skeleton className="h-8 w-56 bg-white/5" />
          </div>
        ) : displayItems.length === 0 ? (
          <div className="flex items-center h-full pl-28">
            <p className="text-zinc-600 italic" style={{ fontSize: "13px", fontFamily: "Inter, sans-serif" }}>
              No members are currently Open to Work.
            </p>
          </div>
        ) : (
          <div
            ref={touchWrapperRef}
            className="overflow-hidden h-full flex items-center"
          >
            <div
              ref={trackRef}
              className="marquee-track flex items-center whitespace-nowrap pl-28"
            >
              {[...displayItems, ...displayItems].map((item, i) => (
                <React.Fragment key={i}>
                  <button
                    onClick={() => onTitleFilter(item.title)}
                    className={`shrink-0 uppercase font-bold cursor-pointer transition-opacity hover:opacity-70${item.seeking ? " ticker-glow-title" : ""}`}
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontSize: "clamp(1rem, 1.9vw, 1.55rem)",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      color: item.seeking ? "#E8184A" : "#e4e4e7",
                      padding: "0 0.85rem",
                      background: "none",
                      border: "none",
                    }}
                  >
                    {item.title}
                  </button>
                  <span
                    className="shrink-0 font-light select-none"
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontSize: "clamp(1.5rem, 2.8vw, 2.2rem)",
                      color: "#E8184A",
                      opacity: 0.4,
                    }}
                  >
                    /
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  }

  // Show a figure while loading or on error; hide only on a confirmed zero.
  const activeOpportunitiesCount = stats?.activeOpportunities ?? 0;
  const showActiveRoles = statsLoading || statsError || activeOpportunitiesCount > 0;
  const referralsCount = referralsAvailable ?? 0;
  const showReferrals = isVerifiedAlumni && referralsCount > 0;

  return (
    <section className="border-y border-white/5 bg-zinc-900/30 backdrop-blur py-6 relative z-20">
      <div className="container mx-auto max-w-6xl px-4 flex flex-wrap justify-center md:justify-start items-center gap-10 md:gap-16">
        {showActiveRoles && (
          <div className="flex flex-col items-center md:items-start">
            {statsLoading ? (
              <Skeleton className="h-9 w-10 mb-1 bg-white/5" />
            ) : (
              <span className="text-3xl font-bold text-white">
                {statsError ? "—" : activeOpportunitiesCount}
              </span>
            )}
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Active Roles</span>
          </div>
        )}

        {showReferrals && (
          <>
            {showActiveRoles && <div className="w-px h-10 bg-white/5 hidden md:block" />}
            <div className="flex flex-col items-center md:items-start">
              <span className="text-3xl font-bold text-white">
                {referralsCount}
              </span>
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Referrals Available</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
